/** Matches all common mobile phone UA tokens */
const MOBILE_REGEX = /mobi|android.*mobile|iphone|ipod|blackberry|iemobile|opera mini/i;

/** * Matches tablet UA tokens. * "Android" alone (without "Mobile") is treated as a tablet. */
const TABLET_REGEX = /ipad|android(?!.*mobile)|tablet/i;

/** * Browser-detection rules. * Each entry: [displayName, identifierRegex, versionCaptureRegex]. */
const BROWSER_RULES = [
  // Chromium-based Edge must come BEFORE Chrome
  ['Edge', /Edg\/|Edge\//, /(?:Edg|Edge)\/([\d.]+)/],
  // Opera must come BEFORE Chrome (OPR token is in Chrome UA too)
  ['Opera', /OPR\/|Opera/, /(?:OPR|Opera)[ /]([\d.]+)/],
  // Samsung Internet
  ['Samsung Internet', /SamsungBrowser\//, /SamsungBrowser\/([\d.]+)/],
  // UC Browser
  ['UC Browser', /UCBrowser\//, /UCBrowser\/([\d.]+)/],
  // Chrome / Chromium
  ['Chrome', /Chrome\//, /Chrome\/([\d.]+)/],
  // Firefox
  ['Firefox', /Firefox\//, /Firefox\/([\d.]+)/],
  // Safari (after Chrome — Chrome UA also contains "Safari")
  ['Safari', /Safari\//, /Version\/([\d.]+).*Safari/],
  // Legacy Internet Explorer
  ['IE', /Trident\/|MSIE /, /(?:MSIE |rv:)([\d.]+)/],
];

/** * Ordered OS-detection rules. Each entry: [displayName, identifierRegex] */
const OS_RULES = [
  ['iOS', /iPhone|iPad|iPod/],
  ['Android', /Android/],
  ['Windows', /Windows NT/],
  ['macOS', /Macintosh|Mac OS X/],
  ['Linux', /Linux/],
];

/** * Parses browser name and version from a User-Agent string. */
export const getBrowserInfo = (ua) => {
  // eslint-disable-next-line no-restricted-syntax -- for...of enables ordered early-exit iteration over BROWSER_RULES
  for (const [name, identifierRegex, versionRegex] of BROWSER_RULES) {
    if (identifierRegex.test(ua)) {
      const versionMatch = ua.match(versionRegex);
      return { name, version: versionMatch ? versionMatch[1] : 'unknown' };
    }
  }
  return { name: 'Unknown', version: 'unknown' };
};

/** * Determines device type from a User-Agent string.  */
export const getDeviceType = (ua) => {
  // Check tablet FIRST — iPad UA contains "Mobile" token which would falsely
  // match MOBILE_REGEX if checked first.
  const isTablet = TABLET_REGEX.test(ua);
  const isMobile = !isTablet && MOBILE_REGEX.test(ua);
  const isDesktop = !isMobile && !isTablet;

  let type;
  if (isMobile) {
    type = 'mobile';
  } else if (isTablet) {
    type = 'tablet';
  } else {
    type = 'desktop';
  }

  return {
    type, isMobile, isTablet, isDesktop,
  };
};

/** * Parses OS name from a User-Agent string. */
export const getOsInfo = (ua) => {
  // eslint-disable-next-line no-restricted-syntax -- for...of enables ordered early-exit iteration over OS_RULES
  for (const [name, regex] of OS_RULES) {
    if (regex.test(ua)) return { name };
  }
  return { name: 'Unknown' };
};

/** * Builds a complete client-context object for ELF logging or any other use. */
export const getClientContext = (userAgent) => {
  // Use ?? (nullish coalescing) so an explicit empty string '' is preserved
  // and not replaced by navigator.userAgent.
  const ua = userAgent ?? (typeof navigator !== 'undefined' ? navigator.userAgent : '') ?? '';

  return {
    userAgent: ua,
    browser: getBrowserInfo(ua),
    device: getDeviceType(ua),
    os: getOsInfo(ua),
  };
};
